import { useState } from "react";
import { X, CheckCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useUser } from "../lib/UserContext";

const COUNTRIES = [
  "United States", "United Kingdom", "France", "Germany", "Italy", "Spain",
  "India", "United Arab Emirates", "Saudi Arabia", "Qatar", "Kuwait",
  "China", "South Korea", "Japan", "Singapore", "Australia", "Canada",
  "Brazil", "Mexico", "Nigeria", "South Africa", "Netherlands", "Switzerland",
  "Turkey", "Indonesia", "Vietnam", "Thailand", "Other",
];

export default function RegisterModal({ open, onClose }) {
  const { setUser } = useUser();
  const [form, setForm] = useState({ name: "", country: "United States", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.phone || !form.country) {
      setError("All fields are required.");
      return;
    }
    setLoading(true);
    try {
      const u = await api.registerUser(form);
      setUser(u);
      setDone(true);
      setTimeout(() => {
        onClose();
        setDone(false);
      }, 1400);
    } catch (err) {
      setError(err?.response?.data?.detail?.toString() || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="register-modal"
      className="fixed inset-0 z-50 bg-[#1C1B1A]/50 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg bg-white border border-[#1C1B1A]" role="dialog" aria-modal="true">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC]">
          <div>
            <div className="eyebrow">Free account</div>
            <div className="font-heading text-xl mt-1">Join Cosmetovigilance</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="register-close"
            className="w-8 h-8 flex items-center justify-center text-[#6E6B65] hover:text-[#1C1B1A]"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="p-10 text-center" data-testid="register-success">
            <CheckCircle size={44} className="mx-auto text-[#40593B]" weight="duotone" />
            <div className="font-heading text-2xl mt-4">You're in.</div>
            <div className="text-sm text-[#6E6B65] mt-1">Welcome, {form.name.split(" ")[0]}.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="eyebrow block mb-1.5">Full name</label>
              <input
                data-testid="register-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2.5 border border-[#E5E2DC] focus:border-[#A66E4E] focus:outline-none focus:ring-1 focus:ring-[#A66E4E] bg-white font-heading text-[15px]"
                placeholder="Maya Iyer"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="eyebrow block mb-1.5">Country</label>
                <select
                  data-testid="register-country"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E5E2DC] focus:border-[#A66E4E] focus:outline-none focus:ring-1 focus:ring-[#A66E4E] bg-white font-heading text-[15px]"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Phone</label>
                <input
                  data-testid="register-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E5E2DC] focus:border-[#A66E4E] focus:outline-none focus:ring-1 focus:ring-[#A66E4E] bg-white font-heading text-[15px]"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Email</label>
              <input
                data-testid="register-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2.5 border border-[#E5E2DC] focus:border-[#A66E4E] focus:outline-none focus:ring-1 focus:ring-[#A66E4E] bg-white font-heading text-[15px]"
                placeholder="maya@example.com"
              />
            </div>
            {error && (
              <div data-testid="register-error" className="text-[12px] text-[#D93636] bg-[#FBEAE9] px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full bg-[#1C1B1A] text-white py-3 text-sm font-semibold tracking-wide uppercase hover:bg-[#A66E4E] transition-colors disabled:opacity-60"
            >
              {loading ? "Creating account…" : "Create free account"}
            </button>
            <p className="text-[11px] text-[#6E6B65] text-center leading-relaxed">
              GCRS trademark. By registering you agree to receive cosmetovigilance bulletins for
              your region. You can opt out anytime.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
